import { useHistory } from 'react-router-dom';
import { useMemo, useEffect, useState } from 'react';
import chunk from 'lodash.chunk';
import { IReportTree } from '../../../store/modules/report/i-report';
import { useActions } from '../../providers/actions-provider/actions.context';

import { EDIT_REPORT_GROUP_PATH, CREATE_REPORT_GROUP_PATH, REPORTS_LIST_PATH } from '../../routing/paths.constants';

import { useStore } from '../../providers/store-provider/state.context';
import findReportInTheTree from '../reports/findReportInTheTree.utils';
import { ICrumb } from '../create-report/interfaces';
import { IBreadcrumbsRoute } from '../../../utils/create-breadcrumbs/create-breadcrumbs.util';
import { useLocalStorage } from '../../providers/local-storage-provider/local-storage.context';
import { USER_ID } from '../../../store/modules/user/user.constants';

interface IUseCreateReportGroup {
  initialValues: {
    tenant: string,
    reportGroup_id: number | null,
    name: string
  };
  isEdit:boolean;
  dataForCrumbs: IBreadcrumbsRoute[] | undefined;
  id: number | null
  reportGroup: IReportTree | null
}

const useCreateReportGroup = ()
: IUseCreateReportGroup => {
  const { currentUser, reportsTree } = useStore();
  const { location: { pathname }, location } = useHistory();
  const { userActions } = useActions();
  const localStorageService = useLocalStorage();
  const isRoot = pathname === `${REPORTS_LIST_PATH}${CREATE_REPORT_GROUP_PATH}`;
  const name = location.pathname.split('/').splice(-3, 1).pop() || '';
  const reportGroup_id = pathname.split('/');
  const isEdit = `/${reportGroup_id[reportGroup_id.length - 1]}` === EDIT_REPORT_GROUP_PATH;
  const [reportGroup, setReportGroup] = useState<IReportTree>();
  const [dataForCrumbs, setDataForCrumbs] = useState<ICrumb[]>();

  useEffect(() => {
    if (reportsTree && isEdit) {
      const result = findReportInTheTree(reportsTree, 'name', name);
      if (result.length) {
        setReportGroup(result[0]);
      }
    }
  }, [pathname, isEdit, reportsTree]);

  useEffect(() => {
    if (!currentUser.data.tenant) {
      const userID: number | null = localStorageService.get(USER_ID);
      if (userID) {
        userActions.getCurrentUser(userID);
      }
    }
  }, []);

  useEffect(() => {
    let dataForCrumb: ICrumb[] = [];
    const initialCrumb = {
      path: REPORTS_LIST_PATH,
      breadcrumbName: 'Reports List',
    };
    if (isRoot) {
      dataForCrumb.push(initialCrumb);
    } else {
      // preparing routes for creation crumbs - without first two static '' and 'reports-list'
      const prepareRoutes = location.pathname.split('/').slice(2);
      // break array by chunk of /name-of-nest and /id
      const chunks = chunk(prepareRoutes, 2);
      let path = REPORTS_LIST_PATH;
      const crumbsWithoutInitial = chunks.map((el) => {
        // each next path need to include previous
        path += `/${el[0]}/${el[1]}`;
        return {
          path,
          breadcrumbName: el[0],
        };
      });
      dataForCrumb = [initialCrumb, ...crumbsWithoutInitial];
      dataForCrumb.pop();
    }
    setDataForCrumbs(dataForCrumb);
  }, [reportsTree, pathname]);

  return useMemo(() => ({
    initialValues: {
      tenant: currentUser.data.tenant,
      // eslint-disable-next-line no-nested-ternary
      reportGroup_id: isRoot ? null
        : isEdit && reportGroup
          ? reportGroup?.reportGroup_id : +reportGroup_id[reportGroup_id.length - 2],
      name: reportGroup?.name ?? '',
    },
    isEdit,
    reportGroup: reportGroup || null,
    dataForCrumbs,
    id: reportGroup?.id ?? null,
  }), [pathname, isEdit, reportGroup, currentUser, dataForCrumbs]);
};

export default useCreateReportGroup;
